import { makeConfigurationCode } from 'src/utils/reminderNumber';
import { isQuickoSource, isRecordedSource } from './config';
import type {
	QuotationOptional,
	QuotationPdfTag,
	QuotationSource,
	SaveQuotationBody,
} from './types';

export type PdfDataLike = {
	quotation?: QuotationPdfTag;
	pdf?: any;
	dati_veicolo?: any;
	dettagli_veicolo?: any;
	lista_offerte?: any[];
	optionals?: any[];
	image_list?: Array<{ url?: string; [key: string]: unknown }>;
	[key: string]: unknown;
};

const str = (v: unknown): string | null =>
	typeof v === 'string' && v.trim() ? v : null;

export const parseAmount = (v: unknown): number | null => {
	if (v == null || v === '') return null;
	if (typeof v === 'number') return Number.isFinite(v) ? v : null;
	const s = String(v).trim();
	const normalized = s.includes(',')
		? s.replace(/\./g, '').replace(',', '.')
		: s;
	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : null;
};

const num = parseAmount;

export const ensureQuotationCode = (
	data: PdfDataLike,
	mint: () => string = makeConfigurationCode
): string | null => {
	if (!isRecordedSource(data?.quotation?.source)) return null;
	data.pdf = data.pdf ?? {};
	if (!data.pdf.quotationCode) data.pdf.quotationCode = mint();
	return data.pdf.quotationCode;
};

const canoneOf = (
	source: QuotationSource,
	data: PdfDataLike,
	offer: any
): number | null => {
	if (isQuickoSource(source)) {
		return num(offer?.canone);
	}

	const key =
		data.pdf?.offerType === 'private' ? 'importo_privato' : 'importo_azienda';

	return num(offer?.[key]);
};

const buildOptionals = (optionals: unknown): QuotationOptional[] =>
	(Array.isArray(optionals) ? optionals : [])
		.filter((o) => str(o?.descrizione))
		.map((o) => ({ descrizione: o.descrizione, valore: num(o.valore) }));

export const servicesToServizi = (
	services: unknown
): Record<string, string> | null => {
	const entries = (typeof services === 'string' ? services : '')
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean)
		.map((line): [string, string] => {
			const sep = line.indexOf(':');
			return sep > 0
				? [line.slice(0, sep).trim(), line.slice(sep + 1).trim()]
				: [line, ''];
		});
	return entries.length ? Object.fromEntries(entries) : null;
};

export const buildQuotationDraft = (
	data: PdfDataLike,
	offer: any,
	services: unknown = data?.pdf?.services
): SaveQuotationBody => {
	const tag = data?.quotation;
	if (!tag?.source) {
		throw new Error('pdfData senza quotation.source');
	}
	const v = tag.vehicle ?? {};
	const dv = data.dati_veicolo ?? {};

	return {
		source: tag.source,
		code: data.pdf?.quotationCode ?? '',
		makeCode: v.makeCode !== undefined ? v.makeCode : str(dv.make_code),
		makeLabel: v.makeLabel ?? dv.marca ?? '',
		modelCode: v.modelCode !== undefined ? v.modelCode : str(dv.model_code),
		modelLabel: v.modelLabel ?? dv.modello ?? '',
		trimLabel: v.trimLabel ?? str(dv.allestimento),
		vehicleCode: v.vehicleCode ?? str(dv.vehicle_code),
		vehicleKind: v.vehicleKind ?? str(dv.vehicle_kind),
		stockVehicleId: v.stockVehicleId ?? null,
		quoteRequestId: v.quoteRequestId ?? null,
		client: data.pdf?.name ?? '',
		vehicleDealer: str(data?.dettagli_veicolo?.dealer),
		// Offerte stock/libere/stellantis usano `offerType`, Quicko `clientType`.
		clientType: data.pdf?.offerType ?? data.pdf?.clientType ?? '',
		noleggiatore: str(offer?.noleggiatore),
		canone: canoneOf(tag.source, data, offer),
		durata: num(offer?.durata),
		kmTotali: num(offer?.km_totali),
		anticipo: num(offer?.anticipo),
		imageUrl: str(data.image_list?.[0]?.url),
		optionals: buildOptionals(data.optionals),
		servizi: servicesToServizi(services),
	};
};
